import React, { useState, useEffect, useRef, useCallback } from 'react';
import './resumeBuild.css';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { templates } from './templates/index';
import { apiFetch } from '../../api/client';
import { useProfileGuard } from '../../hooks/useProfileGuard';
import {
  FiArrowRight, FiArrowLeft, FiCheck, FiX, FiZap, FiAlertCircle, FiLock, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { warmupLatexEngine } from './latexEngine';

const PREFS_SAVE_DELAY = 1200;

const SUPPORTED_JOB_HOSTS = [
  'linkedin.com',
  'indeed.com',
  'joinhandshake.com',
  'greenhouse.io',
  'lever.co',
  'ashbyhq.com',
  'smartrecruiters.com',
  'workable.com'
];

const isSupportedJobUrl = (value) => {
  try {
    const u = new URL(value);
    const host = u.hostname.toLowerCase();
    return SUPPORTED_JOB_HOSTS.some((pattern) => host.includes(pattern));
  } catch (e) {
    return false;
  }
};

const getTemplateNumber = (templateId) => {
  const match = /^resume(\d+)$/.exec(templateId || '');
  return match ? parseInt(match[1], 10) : null;
};

const SECTION_OPTIONS = [
  { id: 'summary', label: 'Summary' },
  { id: 'experience', label: 'Work Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'education', label: 'Education' },
  { id: 'skills', label: 'Skills' },
  { id: 'certs', label: 'Certifications' },
  { id: 'publications', label: 'Publications' },
];

const DEFAULT_SECTIONS = ['summary', 'experience', 'projects', 'education', 'skills'];

const CONTACT_OPTIONS = [
  { id: 'fullName', label: 'Full name' },
  { id: 'headline', label: 'Current title / headline' },
  { id: 'location', label: 'Location' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'github', label: 'GitHub' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'portfolio', label: 'Portfolio' },
];

const buildDefaultGranularSelection = (profile = {}, savedPrefs = null) => {
  const defaults = {
    contactFields: CONTACT_OPTIONS.map((option) => option.id),
    experienceIndices: (profile.experiences || []).map((_, index) => index),
    projectIndices: (profile.projects || []).map((_, index) => index),
    skillIndices: (profile.skills || []).map((_, index) => index),
    educationIndices: (profile.education || []).map((_, index) => index),
    certIndices: (profile.certs || []).map((_, index) => index),
    publicationIndices: (profile.publications || []).map((_, index) => index)
  };
  if (!savedPrefs) return defaults;

  // Merge saved prefs with defaults: keep only indices that still exist in the
  // profile (the user may have added/removed items since last save) and respect
  // saved deselections.
  const merge = (defaultArr, savedArr) => {
    if (!Array.isArray(savedArr)) return defaultArr;
    const validSet = new Set(defaultArr);
    return savedArr.filter((i) => validSet.has(i));
  };

  return {
    contactFields: Array.isArray(savedPrefs.contactFields)
      ? savedPrefs.contactFields.filter((f) => CONTACT_OPTIONS.some((o) => o.id === f))
      : defaults.contactFields,
    experienceIndices: merge(defaults.experienceIndices, savedPrefs.experienceIndices),
    projectIndices: merge(defaults.projectIndices, savedPrefs.projectIndices),
    skillIndices: merge(defaults.skillIndices, savedPrefs.skillIndices),
    educationIndices: merge(defaults.educationIndices, savedPrefs.educationIndices),
    certIndices: merge(defaults.certIndices, savedPrefs.certIndices),
    publicationIndices: merge(defaults.publicationIndices, savedPrefs.publicationIndices)
  };
};

const describeExperience = (exp = {}) =>
  [exp.title, exp.company].filter(Boolean).join(' · ') || 'Untitled role';

const describeEducation = (edu = {}) =>
  [edu.school, [edu.degree, edu.field].filter(Boolean).join(' in ')].filter(Boolean).join(' · ') || 'Untitled education';

const describeCert = (cert = {}) =>
  [cert.name, cert.issuer].filter(Boolean).join(' · ') || 'Untitled certification';

const describePublication = (pub = {}) =>
  [pub.title, pub.venue].filter(Boolean).join(' · ') || 'Untitled publication';

function ResumeBuild() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [job, setJob] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualDescription, setManualDescription] = useState('');

  const [generating, setGenerating] = useState(false);
  const [openingEditor, setOpeningEditor] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const [usage, setUsage] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selectedSections, setSelectedSections] = useState(DEFAULT_SECTIONS);
  const [granularSelection, setGranularSelection] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    contact: true,
    experience: false,
    projects: false,
    skills: false,
    education: false,
    certs: false,
    publications: false
  });

  const toggleSection = (id) => {
    setSelectedSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleExpandedGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const toggleContactField = (field) => {
    setGranularSelection((prev) => ({
      ...prev,
      contactFields: prev.contactFields.includes(field)
        ? prev.contactFields.filter((item) => item !== field)
        : [...prev.contactFields, field]
    }));
  };

  const toggleSelectionIndex = (key, index) => {
    setGranularSelection((prev) => ({
      ...prev,
      [key]: prev[key].includes(index)
        ? prev[key].filter((item) => item !== index)
        : [...prev[key], index].sort((a, b) => a - b)
    }));
  };

  useProfileGuard();

  const prefsLoaded = useRef(false);
  const prefsSaveTimer = useRef(null);

  useEffect(() => {
    apiFetch('/api/resume/usage').then(setUsage).catch(() => {});
    apiFetch('/api/profile')
      .then((data) => {
        const p = data.profile;
        setProfile(p);
        const saved = p?.selectionPrefs || null;
        setGranularSelection(buildDefaultGranularSelection(p, saved));
        prefsLoaded.current = true;

        // Only pre-select sections that actually have content in the profile.
        if (p) {
          const populated = DEFAULT_SECTIONS.filter((id) => {
            if (id === 'summary') return Boolean(p.summary?.trim());
            if (id === 'experience') return (p.experiences || []).length > 0;
            if (id === 'projects') return (p.projects || []).length > 0;
            if (id === 'education') return (p.education || []).length > 0;
            if (id === 'skills') return (p.skills || []).length > 0;
            if (id === 'certs') return (p.certs || []).length > 0;
            if (id === 'publications') return (p.publications || []).length > 0;
            return true;
          });
          setSelectedSections(populated);
        }
      })
      .catch(() => {});
    warmupLatexEngine();
    return () => { if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current); };
  }, []);

  const savePrefs = useCallback((prefs) => {
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      apiFetch('/api/profile', { method: 'PUT', body: { selectionPrefs: prefs } }).catch(() => {});
    }, PREFS_SAVE_DELAY);
  }, []);

  useEffect(() => {
    if (!prefsLoaded.current || !granularSelection) return;
    savePrefs(granularSelection);
  }, [granularSelection, savePrefs]);

  const goToStep2 = () => {
    if (!selectedTemplate) return;
    setStep(2);
    setGenerateError(null);
  };

  const goBack = () => {
    setStep(1);
    setGenerateError(null);
  };

  const scrapeJob = async () => {
    setUrlError(null);
    setJob(null);
    setManualMode(false);

    if (!isSupportedJobUrl(url)) {
      setManualMode(true);
      setUrlError('That does not look like a supported job posting URL. Paste the description manually instead.');
      return null;
    }

    setScraping(true);
    try {
      const data = await apiFetch('/api/resume/job-scrape', { method: 'POST', body: { url } });
      if (data.scrapeFailed) {
        setManualMode(true);
        setUrlError(data.error);
        return null;
      }
      setJob(data);
      return data;
    } catch (err) {
      setManualMode(true);
      setUrlError(err.message);
      return null;
    } finally {
      setScraping(false);
    }
  };

  const confirmManual = () => {
    if (!manualDescription.trim()) return;
    setJob({ title: '', company: '', description: manualDescription.trim() });
    setUrlError(null);
    setManualMode(false);
  };

  const openInEditor = async (template) => {
    setOpeningEditor(true);
    setGenerateError(null);
    try {
      const data = await apiFetch('/api/resume/open-template', {
        method: 'POST',
        body: { templateId: template.id },
      });
      navigate(`/app/editor/${data.resumeId}`);
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      setOpeningEditor(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;

    setGenerateError(null);

    let jobData = job;
    if (!jobData || !jobData.description) {
      if (manualMode && manualDescription.trim()) {
        jobData = { title: '', company: '', description: manualDescription.trim() };
        setJob(jobData);
      } else {
        const scraped = await scrapeJob();
        if (!scraped) return;
        jobData = scraped;
      }
    }

    setGenerating(true);
    try {
      const data = await apiFetch('/api/resume/resume-generate', {
        method: 'POST',
        body: {
          templateId: selectedTemplate.id,
          jobUrl: url,
          jobTitle: jobData.title,
          company: jobData.company,
          jobDescription: jobData.description,
          sections: selectedSections,
          granularSelection: granularSelection || undefined
        }
      });
      navigate(`/app/editor/${data.resumeId}`);
    } catch (err) {
      setGenerateError(err.message);
      apiFetch('/api/resume/usage').then(setUsage).catch(() => {});
    } finally {
      setGenerating(false);
    }
  };

  const remaining = usage ? usage.generationsRemaining : null;
  const maxTemplates = usage?.limits?.maxTemplates ?? templates.length;

  const isTemplateLocked = (template) => {
    if (!usage) return false;
    const idx = templates.findIndex((t) => t.id === template.id);
    return idx === -1 || idx >= maxTemplates;
  };

  const handleTemplateSelect = (template) => {
    if (isTemplateLocked(template)) {
      navigate('/pricing');
      return;
    }
    setSelectedTemplate(template);
  };

  const handleOpenInEditor = (template) => {
    if (isTemplateLocked(template)) {
      navigate('/pricing');
      return;
    }
    openInEditor(template);
  };

  const busy = scraping || generating || openingEditor;
  const granularReady = !!granularSelection;

  const granularGroups = [
    {
      id: 'experience',
      title: 'Jobs',
      sectionId: 'experience',
      stateKey: 'experienceIndices',
      items: (profile?.experiences || []).map((exp, index) => ({ index, label: describeExperience(exp) }))
    },
    {
      id: 'projects',
      title: 'Projects',
      sectionId: 'projects',
      stateKey: 'projectIndices',
      items: (profile?.projects || []).map((proj, index) => ({ index, label: proj.name || `Project ${index + 1}` }))
    },
    {
      id: 'skills',
      title: 'Skills',
      sectionId: 'skills',
      stateKey: 'skillIndices',
      items: (profile?.skills || []).map((skill, index) => ({ index, label: skill || `Skill ${index + 1}` }))
    },
    {
      id: 'education',
      title: 'Education entries',
      sectionId: 'education',
      stateKey: 'educationIndices',
      items: (profile?.education || []).map((edu, index) => ({ index, label: describeEducation(edu) }))
    },
    {
      id: 'certs',
      title: 'Certifications',
      sectionId: 'certs',
      stateKey: 'certIndices',
      items: (profile?.certs || []).map((cert, index) => ({ index, label: describeCert(cert) }))
    },
    {
      id: 'publications',
      title: 'Publications',
      sectionId: 'publications',
      stateKey: 'publicationIndices',
      items: (profile?.publications || []).map((pub, index) => ({ index, label: describePublication(pub) }))
    }
  ];

  return (
    <div className={`resumeBuildContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="buildInner">
        <div className="buildHeader">
          <h1 className="pageTitle">Build a tailored resume</h1>
          <p className="pageSubtitle">
            {step === 1
              ? 'Choose a template to get started'
              : `Tailoring with ${selectedTemplate?.name}`}
          </p>
          {remaining !== null && (
            <span className="remainingPill">
              <FiZap size={12} /> {remaining} generation{remaining === 1 ? '' : 's'} left today
            </span>
          )}
        </div>

        <div className="wizardProgress">
          <div className={`wizardDot ${step >= 1 ? 'active' : ''}`} />
          <div className={`wizardLine ${step >= 2 ? 'active' : ''}`} />
          <div className={`wizardDot ${step >= 2 ? 'active' : ''}`} />
        </div>

        {/* ===== Step 1: Template ===== */}
        {step === 1 && (
          <div className="wizardPanel">
            <div className="wizardPanelTitle">Choose a template</div>
            {generateError && (
              <div className="urlErrorRow"><FiAlertCircle size={14} /> {generateError}</div>
            )}
            <div className="templateGrid">
              {templates.map((template) => {
                const locked = isTemplateLocked(template);
                return (
                <div
                  key={template.id}
                  className={`templateCard ${selectedTemplate?.id === template.id ? 'selected' : ''} ${locked ? 'locked' : ''}`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  {locked && (
                    <div className="templateLockBadge" title="Upgrade to unlock">
                      <FiLock size={12} /> Upgrade
                    </div>
                  )}
                  <button
                    className="previewButton"
                    title="Preview template"
                    onClick={(e) => { e.stopPropagation(); setPreviewTemplate(template); }}
                  >+</button>
                  {!locked && (
                    <button
                      className="editorOpenButton"
                      title="Open in editor"
                      onClick={(e) => { e.stopPropagation(); handleOpenInEditor(template); }}
                      disabled={openingEditor}
                    >
                      <FiArrowRight size={14} />
                    </button>
                  )}
                  {selectedTemplate?.id === template.id && !locked && (
                    <div className="selectedBadge"><FiCheck size={13} /></div>
                  )}
                  <img src={template.preview} alt={template.name} className="templateImage" />
                  <div className="templateMeta">
                    <p className="templateName">{template.name}</p>
                    <span className="templateTag">{template.tag}</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== Step 2: Job URL + Generate ===== */}
        {step === 2 && (
          <div className="wizardPanel wizardPanelNarrow">
            <div className="wizardPanelTitle">Paste the job posting URL</div>
            <p className="urlLabel">LinkedIn, Indeed, Handshake, Greenhouse, Lever, and other major boards are supported. If parsing looks wrong, you can paste the description manually.</p>

            <div className="urlForm">
              <input
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setUrlError(null);
                  setJob(null);
                  setManualMode(false);
                }}
                placeholder="https://www.linkedin.com/jobs/view/... or another job board URL"
                className="urlInput"
                disabled={busy}
              />
            </div>

            {urlError && (
              <div className="urlErrorRow"><FiAlertCircle size={14} /> {urlError}</div>
            )}

            {manualMode && !job && (
              <div className="manualFallback">
                <p className="manualLabel">Paste the job description manually</p>
                <textarea
                  rows={7}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Copy the full job description and paste it here..."
                />
                <button className="pillBtnB solid" onClick={confirmManual} disabled={!manualDescription.trim()}>
                  Use this description
                </button>
              </div>
            )}

            {job && (
              <div className="jobConfirm">
                <div className="jobConfirmHead">
                  <div>
                    <div className="jobConfirmTitle">{job.title || 'Job description added'}</div>
                    {job.company && <div className="jobConfirmCompany">{job.company}</div>}
                  </div>
                  <button
                    className="jobClear"
                    onClick={() => { setJob(null); setManualMode(false); setManualDescription(''); }}
                    title="Clear"
                  >
                    <FiX size={15} />
                  </button>
                </div>
                <p className="jobConfirmDesc">
                  {job.description.slice(0, 420)}{job.description.length > 420 ? '…' : ''}
                </p>
                <button
                  className="pillBtnB ghost subtle"
                  onClick={() => {
                    setManualDescription(job.description || '');
                    setJob(null);
                    setManualMode(true);
                  }}
                  type="button"
                >
                  Parsed wrong? Paste manually instead
                </button>
              </div>
            )}

            {generateError && (
              <div className="urlErrorRow"><FiAlertCircle size={14} /> {generateError}</div>
            )}

            <div className="sectionPicker">
              <p className="sectionPickerLabel">Sections to include</p>
              <p className="sectionPickerHint">Pick what goes on this resume — we&rsquo;ll only add sections you have content for.</p>
              <div className="sectionPickerChips">
                {SECTION_OPTIONS.map((s) => {
                  const on = selectedSections.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`sectionChip ${on ? 'on' : ''}`}
                      onClick={() => toggleSection(s.id)}
                    >
                      {on && <FiCheck size={12} />} {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="granularPicker">
              <p className="sectionPickerLabel">Granular selection</p>
              <p className="sectionPickerHint">
                Checked items stay eligible for AI selection. Unchecked items are excluded completely.
              </p>

              {!granularReady ? (
                <p className="granularEmpty">Loading your profile selections…</p>
              ) : (
                <div className="granularGroups">
                  <div className="granularGroup">
                    <button
                      type="button"
                      className="granularGroupHeader"
                      onClick={() => toggleExpandedGroup('contact')}
                    >
                      <span>Header fields</span>
                      <span className="granularGroupMeta">
                        {granularSelection.contactFields.length}/{CONTACT_OPTIONS.length}
                        {expandedGroups.contact ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                      </span>
                    </button>
                    {expandedGroups.contact && (
                      <div className="granularOptions">
                        {CONTACT_OPTIONS.map((option) => {
                          const on = granularSelection.contactFields.includes(option.id);
                          return (
                            <button
                              key={option.id}
                              type="button"
                              className={`granularOption ${on ? 'on' : ''}`}
                              onClick={() => toggleContactField(option.id)}
                            >
                              {on && <FiCheck size={12} />} {option.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {granularGroups
                    .filter((group) => selectedSections.includes(group.sectionId))
                    .map((group) => {
                      const selectedCount = granularSelection[group.stateKey]?.length || 0;
                      return (
                        <div key={group.id} className="granularGroup">
                          <button
                            type="button"
                            className="granularGroupHeader"
                            onClick={() => toggleExpandedGroup(group.id)}
                          >
                            <span>{group.title}</span>
                            <span className="granularGroupMeta">
                              {selectedCount}/{group.items.length}
                              {expandedGroups[group.id] ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
                            </span>
                          </button>
                          {expandedGroups[group.id] && (
                            group.items.length ? (
                              <div className="granularOptions stacked">
                                {group.items.map((item) => {
                                  const on = granularSelection[group.stateKey].includes(item.index);
                                  return (
                                    <button
                                      key={`${group.id}-${item.index}`}
                                      type="button"
                                      className={`granularOption row ${on ? 'on' : ''}`}
                                      onClick={() => toggleSelectionIndex(group.stateKey, item.index)}
                                    >
                                      <span className="granularRowLabel">
                                        {on && <FiCheck size={12} />} {item.label}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="granularEmpty">No {group.title.toLowerCase()} in your profile yet.</p>
                            )
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {remaining === 0 && (
              <p className="limitNote">
                You&rsquo;ve used today&rsquo;s generations.{' '}
                <span className="limitLink" onClick={() => navigate('/pricing')}>Upgrade for more</span>.
              </p>
            )}
          </div>
        )}

        {/* ===== Wizard navigation ===== */}
        <div className="wizardNav">
          {step === 2 ? (
            <button type="button" className="wizardNavBtn back" onClick={goBack} disabled={busy}>
              <FiArrowLeft size={16} /> Back
            </button>
          ) : (
            <span />
          )}

          {step === 1 ? (
            <button
              type="button"
              className="wizardNavBtn next"
              onClick={goToStep2}
              disabled={!selectedTemplate}
            >
              Next <FiArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              className="wizardNavBtn generate"
              onClick={handleGenerate}
              disabled={!url.trim() || busy || remaining === 0 || selectedSections.length === 0}
            >
              {generating ? (
                <><span className="miniSpinner dark" /> Tailoring…</>
              ) : scraping ? (
                <><span className="miniSpinner dark" /> Reading job…</>
              ) : (
                <><FiZap size={15} /> Generate resume</>
              )}
            </button>
          )}
        </div>
      </div>

      {previewTemplate && (
        <div className="overlayBackdrop" onClick={() => setPreviewTemplate(null)}>
          <div className="overlayContent" onClick={(e) => e.stopPropagation()}>
            <button className="overlayClose" onClick={() => setPreviewTemplate(null)}>✕</button>
            <h2 className="overlayTitle">{previewTemplate.name}</h2>
            <img src={previewTemplate.preview} alt={previewTemplate.name} className="overlayImage" />
            <button
              className="overlaySelectBtn"
              onClick={() => { handleTemplateSelect(previewTemplate); setPreviewTemplate(null); }}
              disabled={isTemplateLocked(previewTemplate)}
            >
              {isTemplateLocked(previewTemplate) ? 'Upgrade to Use' : 'Use This Template'}
            </button>
            {!isTemplateLocked(previewTemplate) && (
              <button
                className="overlaySelectBtn secondary"
                onClick={() => { handleOpenInEditor(previewTemplate); setPreviewTemplate(null); }}
                disabled={openingEditor}
              >
                Open in Editor
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ResumeBuild;
